import React from 'react';
import { brandGradients, brandTextGradients } from '../theme/palette';

const WhyChooseUs = () => {
  const reasons = [
    {
      icon: 'fas fa-clock',
      title: 'Timely & Accurate Reports',
      description: 'We deliver comprehensive reports within committed timeframes with excellent time management and attention to detail.',
      color: 'from-blue-500 to-blue-600',
      features: ['On-time delivery', 'Accurate data', 'Quality assurance']
    },
    {
      icon: 'fas fa-shield-alt',
      title: 'High Confidentiality & Security',
      description: 'Maintain highest degree of confidentiality and security in all records and reports. Customer data is never disclosed.',
      color: 'from-green-500 to-green-600',
      features: ['100% confidential', 'Secure processes', 'Data protection']
    },
    {
      icon: 'fas fa-network-wired',
      title: 'Strong Network Across States',
      description: 'Extensive network coverage across Andhra Pradesh, Telangana, Karnataka, and Tamil Nadu with local expertise.',
      color: 'from-purple-500 to-purple-600',
      features: ['4 states coverage', 'Local presence', 'Wide network']
    },
    {
      icon: 'fas fa-user-graduate',
      title: 'Experienced Professionals',
      description: 'Team of highly skilled experts including Chartered Accountants, Lawyers, and retired Police professionals.',
      color: 'from-indigo-500 to-indigo-600',
      features: ['Expert team', 'Proven experience', 'Professional approach']
    },
    {
      icon: 'fas fa-dollar-sign',
      title: 'Affordable & Competitive Services',
      description: 'Competitive pricing with personalized services tailored to customer needs without compromising on quality.',
      color: 'from-orange-500 to-red-500',
      features: ['Competitive rates', 'Value for money', 'Flexible pricing']
    },
    {
      icon: 'fas fa-award',
      title: 'Proven Track Record',
      description: 'Empaneled with SBI, Karnataka Bank and trusted by financial institutions across South India since 2019.',
      color: 'from-teal-500 to-cyan-500',
      features: ['Bank partnerships', 'Proven results', 'Client satisfaction']
    }
  ];

  return (
    <section className="py-16 lg:py16 bg-gradient-to-br from-gray-50 to-blue-50">
  <div className="container">
        {/* Section Header */}
        <div className="text-center mb-10 animate-on-scroll">
         <span className="text-[#00a4f4] text-xl font-bold"style={{ color: '#ff70a6' }}>
            Why Choose Us
          </span>
          <h2 className="text-4xl lg:text-6xl font-black text-gray-800 mt-4 mb-8 leading-tight">
            Why RLDS Pvt Ltd is Your
            <span className="block text-blue-600">Best Choice</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Our commitment to excellence, confidentiality, and professional service delivery makes us 
            the preferred partner for verification and due diligence services across South India.
          </p>
        </div>

        {/* Reasons Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-10">
          {reasons.map((reason, index) => (
            <div 
              key={index} 
              className="group relative p-8 bg-white rounded-3xl shadow-1xl hover:shadow-1xl transition-all duration-500 animate-on-scroll border border-gray-100 hover:border-blue-200 overflow-hidden"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Background Gradient on Hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${reason.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>

              <div className="relative z-10">
                {/* Icon */}
                <div className={`w-20 h-20 bg-gradient-to-r ${reason.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-xl`}>
                  <i className={`${reason.icon} text-white text-3xl`}></i>
                </div>

                {/* Content */}
                <h3 className="text-2xl font-bold text-gray-800 mb-4 group-hover:text-blue-600 transition-colors duration-300">
                  {reason.title}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  {reason.description}
                </p>

                {/* Features */}
                <ul className="space-y-2">
                  {reason.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600">
                      <i className="fas fa-check-circle text-green-500 mr-2 text-xs"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Comparison Section */}
        <div className="animate-on-scroll">
          <div className="bg-white rounded-3xl shadow-1xl border border-gray-100 overflow-hidden">
            <div
              className="p-8 text-white text-center"
              style={{ background: brandGradients.panel }}
            >
              <h3 className="text-3xl font-bold mb-4">What Sets Us Apart</h3>
              <p className="text-xl opacity-90">Professional excellence that makes the difference</p>
            </div>

            <div className="p-8 lg:p-12">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 text-center">
                <div className="p-6">
                  <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <i className="fas fa-certificate text-blue-600 text-2xl"></i>
                  </div>
                  <h4 className="font-bold text-gray-800 text-lg mb-2">Bank Empaneled</h4>
                  <p className="text-gray-600 text-sm">SBI & Karnataka Bank certified partner</p>
                </div>

                <div className="p-6">
                  <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <i className="fas fa-users text-green-600 text-2xl"></i>
                  </div>
                  <h4 className="font-bold text-gray-800 text-lg mb-2">Expert Team</h4>
                  <p className="text-gray-600 text-sm">CAs, Lawyers & Ex-Police professionals</p>
                </div>

                <div className="p-6">
                  <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <i className="fas fa-map text-purple-600 text-2xl"></i>
                  </div>
                  <h4 className="font-bold text-gray-800 text-lg mb-2">Wide Coverage</h4>
                  <p className="text-gray-600 text-sm">25+ cities across 4 states</p>
                </div>

                <div className="p-6">
                  <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <i className="fas fa-handshake text-orange-600 text-2xl"></i>
                  </div>
                  <h4 className="font-bold text-gray-800 text-lg mb-2">Trust & Reliability</h4>
                  <p className="text-gray-600 text-sm">Proven track record since 2019</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-10 text-center animate-on-scroll">
          <div className="rounded-3xl p-12 text-white" style={{ background: brandGradients.panel }}>
            <h3 className="text-3xl lg:text-4xl font-bold mb-6">
              Ready to Experience Professional Service?
            </h3>
            <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
              Join hundreds of satisfied clients who trust RLDS Pvt Ltd for their verification 
              and investigation needs across South India.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-blue-600 px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-xl">
                <i className="fas fa-phone mr-2"></i>
                Contact Us Today
              </button>
              <button className="border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-blue-600 transition-all duration-300">
                <i className="fas fa-file-alt mr-2"></i>
                Request Quote
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;